import { z } from 'zod';
import { DAYS_MAX, PCT_MAX, PG_INT_MAX, QUANTITY_MAX, TEXT_LIMITS } from '../limits.js';
import { IsoTimestampSchema, MoneyCentsSchema, SchemaVersionSchema } from './shared.js';
// QA 2026-10-01 F-08 — every number in a published pricing config is bounded
// (money via MoneyCentsSchema, percentages 0..100, day counts ten years), so a
// typo in the admin form is a field error instead of a stored config that
// prices every quote wrong.
const IncludedAssessmentsSchema = z.number().int().nonnegative().max(QUANTITY_MAX);
const PctSchema = z.number().int().nonnegative().max(PCT_MAX);
const ThresholdDaysSchema = z.number().int().positive().max(DAYS_MAX);
const DisplayNameSchema = z.string().max(TEXT_LIMITS.name);
/** Product description cap (one or two sentences). */
export const PRODUCT_DESCRIPTION_MAX = 300;
// Quote PDF fix run (2026-10-27, A1) — the one-line description printed under
// the product name on customer documents. Optional so configs published
// before it existed still validate; documents fall back to the canonical copy
// in `pricing/catalog-copy.ts`.
const ProductDescriptionSchema = z.string().max(PRODUCT_DESCRIPTION_MAX).optional();
/** Volume tiers and payment terms in one config. */
const PRICING_LIST_MAX = 50;
export const PricingConfigSchema = z.object({
    id: z.string(),
    type: z.literal('pricing_config'),
    schemaVersion: SchemaVersionSchema,
    configVersion: z.string(),
    effectiveFrom: IsoTimestampSchema,
    effectiveUntil: IsoTimestampSchema.optional(),
    products: z.object({
        // `employeeAdderPerUnit` is the price (cents) for each ADDITIONAL assessment
        // beyond `includedAssessments` (the count bundled into the flat unitPrice and
        // shown as an "X included" note). Both are optional so configs published
        // before the additional-assessments model still validate (the field defaults
        // to 0 / no included note in consumers).
        eh: z.object({
            displayName: DisplayNameSchema,
            description: ProductDescriptionSchema,
            unitPrice: MoneyCentsSchema,
            includedAssessments: IncludedAssessmentsSchema.optional(),
            employeeAdderPerUnit: MoneyCentsSchema.optional(),
        }),
        ih: z.object({
            displayName: DisplayNameSchema,
            description: ProductDescriptionSchema,
            unitPrice: MoneyCentsSchema,
            includedAssessments: IncludedAssessmentsSchema.optional(),
            employeeAdderPerUnit: MoneyCentsSchema,
        }),
        sp: z.object({
            displayName: DisplayNameSchema,
            description: ProductDescriptionSchema,
            unitPrice: MoneyCentsSchema,
            includedAssessments: IncludedAssessmentsSchema.optional(),
            employeeAdderPerUnit: MoneyCentsSchema,
        }),
        ta: z.object({
            displayName: DisplayNameSchema,
            description: ProductDescriptionSchema,
            baseFeePerTeam: MoneyCentsSchema,
            feePerIndividual: MoneyCentsSchema,
        }),
    }),
    volumeTiers: z
        .array(z.object({
        // Quantities are JSON, not int4, but stay inside the int4 range so
        // nothing downstream can overflow.
        minQty: z.number().int().nonnegative().max(PG_INT_MAX),
        maxQty: z.number().int().nonnegative().max(PG_INT_MAX),
        discountPct: PctSchema,
    }))
        .max(PRICING_LIST_MAX),
    bundleDiscounts: z.record(PctSchema),
    setupFee: MoneyCentsSchema,
    availablePaymentTerms: z.array(z.string().max(TEXT_LIMITS.tag)).max(PRICING_LIST_MAX),
    defaultPaymentTerm: z.string().max(TEXT_LIMITS.tag),
    industries: z.array(z.string().max(TEXT_LIMITS.industry)).max(TEXT_LIMITS.listMax),
    commissionStructure: z.object({
        newDealPct: PctSchema,
        renewalYear2Pct: PctSchema,
        renewalYear3Pct: PctSchema,
        renewalYear4Pct: PctSchema,
        renewalCapYears: z.number().int().nonnegative().max(PCT_MAX),
    }),
    forecastProbabilities: z.object({
        proposalPending: z.number().min(0).max(1),
        signaturePending: z.number().min(0).max(1),
        signed: z.number().min(0).max(1),
        intakeSubmitted: z.number().min(0).max(1),
        active: z.number().min(0).max(1),
    }),
    thresholds: z.object({
        submissionExpiryDays: ThresholdDaysSchema,
        renewalReminderPrimaryDays: ThresholdDaysSchema,
        renewalReminderUrgentDays: ThresholdDaysSchema,
        renewalReminderEscalationDays: ThresholdDaysSchema,
        refundWindowDays: ThresholdDaysSchema,
        commissionPendingDigestDays: ThresholdDaysSchema,
        approvalAutoExpireDays: ThresholdDaysSchema,
        invoiceOverdueReminderDays: ThresholdDaysSchema,
    }),
    /**
     * T3-014O — Per-transition business-rule toggles. JSONB column on the
     * pricing_config table. First inhabitant: `engagedRequiresActivity`
     * (defaults to false; admin re-enables by publishing a new config
     * version). See state-machines/account.ts canTransition for the read
     * path.
     */
    transitionGuards: z.object({
        engagedRequiresActivity: z.boolean(),
    }),
    createdAt: IsoTimestampSchema,
    createdBy: z.string(),
    updatedAt: IsoTimestampSchema,
    updatedBy: z.string(),
    version: z.number().int().positive(),
});
/**
 * QA 2026-10-01 F-08 — the publish payload (`POST /api/v1/pricing-config/publish`
 * body.payload): the config fields an admin edits, without the row metadata.
 */
export const PricingConfigPayloadSchema = PricingConfigSchema.pick({
    configVersion: true,
    products: true,
    volumeTiers: true,
    bundleDiscounts: true,
    setupFee: true,
    availablePaymentTerms: true,
    defaultPaymentTerm: true,
    industries: true,
    commissionStructure: true,
    forecastProbabilities: true,
    thresholds: true,
    transitionGuards: true,
});
//# sourceMappingURL=pricing-config.js.map